/**
 * useGoogleMaps - Singleton hook for loading Google Maps JavaScript API
 * Uses the new functional API (setOptions + importLibrary)
 */

import { useState, useEffect } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

let configured = false;
let loadPromise: Promise<google.maps.PlacesLibrary> | null = null;

export function useGoogleMaps(): { isLoaded: boolean; error: string | null } {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If already loaded globally
    if (typeof google !== 'undefined' && google.maps?.places) {
      setIsLoaded(true);
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setError('Google Maps API key not configured');
      return;
    }

    if (!loadPromise) {
      if (!configured) {
        // Force English (US) results regardless of the browser locale — otherwise
        // a pt-BR browser gets "Nova Iorque" instead of "New York" and we'd store
        // a non-canonical city name. This app is US/English-only.
        setOptions({ key: apiKey, language: 'en', region: 'US' });
        configured = true;
      }
      loadPromise = importLibrary('places');
    }

    loadPromise
      .then(() => {
        setIsLoaded(true);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load Google Maps');
      });
  }, []);

  return { isLoaded, error };
}
